/* =========================================================================
 * GlobalNetwork — homepage centerpiece.
 *
 * Reads:
 *   data/stats                  → top-line counters (observations, devices)
 *   data/location/areas/*       → 250km area centers for the world map
 *
 * Render strategy:
 *   - Counter strip on top (3 big numbers, no chrome)
 *   - Below: styled GoogleMap with all areas as small primary-blue dots
 *
 * Visual aim: Planet Labs / scientific-dashboard, not the Material Kit demo.
 * Custom map style strips POIs + business labels for clean data visualization.
 * ========================================================================= */

import { useEffect, useMemo, useState } from "react";
import { GoogleMap, useJsApiLoader, MarkerF } from "@react-google-maps/api";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { db } from "configuration/firebase";

// ---------------------------------------------------------------------------
// Map style — clean, navy-ish, low contrast. Hides POIs / business labels so
// the contributor dots are the only thing that pops.
// Generated to match the redesign brief's Planet Labs / scientific aesthetic.
// ---------------------------------------------------------------------------

const MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#F8FAFC" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#FFFFFF" }] },
  {
    featureType: "administrative.country",
    elementType: "geometry.stroke",
    stylers: [{ color: "#CBD5E1" }],
  },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#F1F5F9" }] },
  { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#DBEAFE" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#64748B" }] },
];

const MAP_CONTAINER = { width: "100%", height: "100%", minHeight: 460 };
const DEFAULT_CENTER = { lat: 20, lng: 0 }; // roughly world-centered
const DEFAULT_ZOOM = 2;

// Marker icon factory — relies on google.maps.SymbolPath which is only
// available after the JS API has loaded. Called inside render after isLoaded.
function dotIcon() {
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: 5,
    fillColor: "#2563FF",
    fillOpacity: 0.85,
    strokeColor: "#FFFFFF",
    strokeWeight: 1.5,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GlobalNetwork() {
  const [areas, setAreas] = useState([]);
  const [stats, setStats] = useState(null);

  // Areas — one-shot fetch; the homepage doesn't need realtime here.
  useEffect(() => {
    let cancelled = false;
    db.collection("data")
      .doc("location")
      .collection("areas")
      .get()
      .then((snap) => {
        if (cancelled) return;
        const list = snap.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              center: data.center,
              distance: data.distance,
            };
          })
          .filter((a) => a.center && typeof a.center.lat === "number");
        setAreas(list);
      })
      .catch(() => {
        /* leave areas empty; the rest of the section still renders */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Stats — live subscription so counters tick up over time.
  useEffect(() => {
    const unsubscribe = db
      .collection("data")
      .doc("stats")
      .onSnapshot(
        (snap) => setStats(snap.data() || {}),
        () => setStats({})
      );
    return unsubscribe;
  }, []);

  const { isLoaded } = useJsApiLoader({
    // eslint-disable-next-line no-undef
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    id: "google-map-script",
  });

  const counters = useMemo(() => {
    const totalObservations = sumUploadCounts(stats?.uploads);
    return [
      {
        label: "Observations",
        value: formatBigNumber(totalObservations),
        sub: "across every sensor",
      },
      {
        label: "Active areas",
        value: areas.length.toLocaleString(),
        sub: "250 km contribution zones",
      },
      {
        label: "Devices",
        value: formatBigNumber(stats?.mobileDevices || 0),
        sub: "contributing worldwide",
      },
    ];
  }, [areas, stats]);

  return (
    <MKBox
      component="section"
      py={{ xs: 8, md: 12 }}
      px={{ xs: 2, md: 4 }}
      sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[200]}` })}
    >
      <Container>
        <Grid container justifyContent="center" textAlign="center" mb={{ xs: 5, md: 7 }}>
          <Grid item xs={12} md={8}>
            <MKTypography
              variant="overline"
              color="primary"
              fontWeight="bold"
              sx={{ letterSpacing: "0.1em" }}
            >
              Global network
            </MKTypography>
            <MKTypography
              variant="h2"
              color="dark"
              mt={1}
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
            >
              See the network as it grows
            </MKTypography>
            <MKTypography variant="body1" color="text" mt={2} sx={{ maxWidth: 640, mx: "auto" }}>
              Every dot is a 250 km zone where contributors have shared environmental observations.
              Real-time, anonymous, scientifically aggregated.
            </MKTypography>
          </Grid>
        </Grid>

        {/* Counter strip */}
        <Grid container spacing={2} mb={{ xs: 4, md: 5 }}>
          {counters.map((c) => (
            <Grid item xs={12} sm={4} key={c.label}>
              <MKBox
                sx={({ palette, borders }) => ({
                  p: 3,
                  textAlign: "center",
                  borderRadius: borders.borderRadius.lg,
                  background: `linear-gradient(180deg, ${palette.white.main} 0%, ${palette.grey[100]} 100%)`,
                  border: `1px solid ${palette.grey[200]}`,
                })}
              >
                <MKTypography
                  variant="h2"
                  color="dark"
                  sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}
                >
                  {c.value}
                </MKTypography>
                <MKTypography variant="overline" color="primary" fontWeight="bold">
                  {c.label}
                </MKTypography>
                <MKTypography variant="body2" color="text" mt={0.5}>
                  {c.sub}
                </MKTypography>
              </MKBox>
            </Grid>
          ))}
        </Grid>

        {/* Map */}
        <MKBox
          sx={({ palette, borders }) => ({
            position: "relative",
            borderRadius: borders.borderRadius.lg,
            overflow: "hidden",
            border: `1px solid ${palette.grey[200]}`,
            boxShadow: "0 24px 48px -24px rgba(15, 23, 42, 0.18)",
            height: { xs: 340, md: 520 },
            background: palette.grey[100],
          })}
        >
          {!isLoaded ? (
            <MKBox
              sx={{
                width: "100%",
                height: "100%",
                display: "grid",
                placeItems: "center",
                color: "text.secondary",
                fontSize: 14,
              }}
            >
              Loading map…
            </MKBox>
          ) : (
            <GoogleMap
              mapContainerStyle={MAP_CONTAINER}
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              options={{
                styles: MAP_STYLE,
                disableDefaultUI: true,
                gestureHandling: "cooperative",
                minZoom: 2,
              }}
            >
              {areas.map((a) => (
                <MarkerF key={a.id} position={a.center} icon={dotIcon()} />
              ))}
            </GoogleMap>
          )}
        </MKBox>
      </Container>
    </MKBox>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sumUploadCounts(uploads) {
  if (!uploads || typeof uploads !== "object") return 0;
  return Object.values(uploads).reduce((acc, v) => acc + (typeof v === "number" ? v : 0), 0);
}

function formatBigNumber(n) {
  if (!n || n < 1000) return (n || 0).toLocaleString();
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}K`;
  if (n < 1_000_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return `${(n / 1_000_000_000).toFixed(1)}B`;
}
